/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useState } from 'react';

import { css, jsx } from '@compiled/react';
import endOfMonth from 'date-fns/endOfMonth';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import startOfMonth from 'date-fns/startOfMonth';

import Button, { IconButton } from '@atlaskit/button/new';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { formatIsoLocalDate, formatWithLocale } from '@atlassian/townsquare-date-utils/date-format';

import {
	getInitialMonthPickerDate,
	getMonthPickerDate,
	useDateLocale,
} from '../../../common/utils/utils';
import type { CalendarProps, CalendarState } from '../../../types';

const yearPickerContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	marginBottom: 'space.200',
});

const monthPickerContainerStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'space-around',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'> button': {
		flex: '0 0 24%',
		height: '70px',
		marginBottom: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		'> span': {
			alignSelf: 'center',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'> #previously-selected-month-option': {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.subtle'),
	},
});

const monthPickerWrapperStyles = xcss({
	padding: 'space.200',
});

const getMonthNames = (locale: Locale) => {
	const today = new Date();
	const months = [];
	for (let i = 0; i < 12; i++) {
		months.push({
			month: i,
			name: formatWithLocale(new Date(today.getFullYear(), i), 'MMM', locale),
		});
	}
	return months;
};

export const MonthPicker = (props: CalendarProps): JSX.Element => {
	const [date, setDate] = useState(getInitialMonthPickerDate(props));
	const previousDate = getMonthPickerDate(props.previousDate);
	const locale = useDateLocale();
	const { selectedDate, dateType, onSelect, withTimeScale, minDate, maxDate } = props;
	const { month, year, initialYear } = date;
	const MONTHS_DATA = getMonthNames(locale);

	const onDateChange = useCallback(
		(newDate: CalendarState) => {
			setDate(newDate);
			const newDateObject = new Date(newDate.year, newDate.month);
			const newDateInterval = {
				start: formatIsoLocalDate(startOfMonth(newDateObject), locale),
				end: formatIsoLocalDate(endOfMonth(newDateObject), locale),
			};
			onSelect(newDateInterval);
		},
		[onSelect, locale],
	);

	const isMonthDisabled = useCallback(
		(isSelected: boolean, monthItem: number) => {
			const currentMonth = new Date(year, monthItem);
			const monthStart = startOfMonth(currentMonth);
			const monthEnd = endOfMonth(currentMonth);

			if (minDate && monthEnd < minDate) {
				return true;
			}
			if (maxDate && monthStart > maxDate) {
				return true;
			}

			if (!selectedDate) {
				return false;
			}

			return (
				!isSelected &&
				(dateType === 'start'
					? isBefore(selectedDate, currentMonth)
					: isAfter(selectedDate, currentMonth))
			);
		},
		[dateType, selectedDate, year, minDate, maxDate],
	);

	return (
		<Box xcss={monthPickerWrapperStyles}>
			{withTimeScale && (
				<Box xcss={yearPickerContainerStyles} testId="year-picker-container">
					<IconButton
						appearance="subtle"
						spacing="compact"
						onClick={() => setDate({ initialYear, month, year: year - 1 })}
						label="Button to see previous year"
						icon={(iconProps) => <ChevronLeftIcon {...iconProps} size="small" />}
					/>
					<b>{year}</b>
					<IconButton
						appearance="subtle"
						spacing="compact"
						onClick={() => setDate({ initialYear, month, year: year + 1 })}
						label="Button to see next year"
						icon={(iconProps) => <ChevronRightIcon {...iconProps} size="small" />}
					/>
				</Box>
			)}
			<div css={monthPickerContainerStyles} data-testid="month-picker-container" role="radiogroup">
				{MONTHS_DATA.map((item) => {
					const isSelected = date.initialYear === year && month === item.month;
					const isPreviouslySelected = previousDate
						? previousDate.year === year && previousDate.month === item.month
						: false;
					if (isPreviouslySelected) {
						return (
							<Button
								id="previously-selected-month-option"
								appearance="subtle"
								key={item.month}
								testId="previously-selected-month-option"
								aria-checked={false}
								aria-label={item.name}
								role="radio"
							>
								{item.name}
							</Button>
						);
					}
					return (
						<Button
							key={item.month}
							appearance="subtle"
							onClick={() => onDateChange({ initialYear: year, year, month: item.month })}
							isSelected={isSelected}
							isDisabled={isMonthDisabled(isSelected, item.month)}
							testId={isSelected ? 'selected-month-option' : 'month-option'}
							aria-checked={isSelected}
							aria-label={item.name}
							role="radio"
						>
							{item.name}
						</Button>
					);
				})}
			</div>
		</Box>
	);
};
